import { Box, CircularProgress } from "@mui/material";
import { useEffect, useState } from "react";
import {
  fetchPermissions,
  fetchPermissionDependencies,
  fetchRolesPermissions,
  updateRolesAndPermissions,
  PermissionCategory,
} from "../services/RolePermissionService";
import CustomButton from "./common/CustomButton";
import RolePermissionTable from "./RolePermissionTable";
import ToastNotification from "./ToastNotification";
import { COLORS } from "../assets/theme";

const RolePermissionContainer = () => {
  const [permissions, setPermissions] = useState<Record<string, string[]>>({});
  const [permissionCategories, setPermissionCategories] = useState<PermissionCategory[]>([]);
  const [dependencies, setDependencies] = useState<Record<string, string[]>>({});
  const [roles, setRoles] = useState<string[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchData = async () => {
      const startTime = Date.now();
      setLoading(true);
      try {
        const [{ rolesPermissions, roleNames }, categoriesData, depsData] = await Promise.all([
          fetchRolesPermissions(),
          fetchPermissions(),
          fetchPermissionDependencies(),
        ]);

        const depsMap = depsData.reduce<Record<string, string[]>>((acc, d) => {
          acc[d.permission] = d.autoEnabledPermissions;
          return acc;
        }, {});

        setPermissionCategories(categoriesData);
        setPermissions(rolesPermissions);
        setDependencies(depsMap);
        setRoles(roleNames);
      } catch (err: unknown) {
        console.error("Error fetching data:", err);
        ToastNotification.error(
          "Failed to fetch roles and permissions. Please try again."
        );
      } finally {
        const elapsed = Date.now() - startTime;
        const delay = 800 - elapsed;
        if (delay > 0) {
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handlePermissionChange = (permissionName: string, roleName: string) => {
    setPermissions((prev) => {
      const updated = { ...prev };
      const rolePermissions = updated[roleName] || [];

      if (rolePermissions.includes(permissionName)) {
        updated[roleName] = rolePermissions.filter(
          (perm) => perm !== permissionName
        );
      } else {
        updated[roleName] = [...rolePermissions, permissionName];
      }

      return updated;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateRolesAndPermissions(permissions);
      ToastNotification.success("Permissions saved successfully!");
    } catch (error: any) {
      ToastNotification.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          width: "90%",
        }}
      >
        <CircularProgress size={64} sx={{ color: COLORS.GOLD }} />
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          padding: { xs: "12px 4px", md: "32px" },
          mx: { xs: -2, sm: 0 },
          backgroundColor: "#fff",
          borderRadius: "8px",
        }}
      >
        <Box
          className="table-container"
          sx={{
            overflowX: "auto",
          }}
        >
          <RolePermissionTable
            roles={roles}
            permissions={permissions}
            permissionCategories={permissionCategories}
            dependencies={dependencies}
            onPermissionChange={handlePermissionChange}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          marginTop: { xs: "8px", md: "10px" },
        }}
      >
        <CustomButton
          loading={saving}
          label="Save"
          textColor="#fff"
          bgColor="#A39455"
          hoverBgColor="#8B7D44"
          borderRadius={2}
          onClick={handleSave}
          minWidth={120}
          sx={{ width: { xs: "100%", sm: "auto" } }}
          permission="PUT_PERMISSION"
        />
      </Box>
    </>
  );
};

export default RolePermissionContainer;
